import jsPDF from "jspdf";
import pdfLayoutService from "../services/pdfLayout";
import {
  GRUPO_ATIVIDADE_FLORESTAL_LABEL,
  getAtividadeEmpreendimentoLabel,
  montarEnderecoEmpreendimento,
} from "../utils/constants";
import { formatCpfCnpj, formatPhone } from "../utils/formatters";

const PAGE_WIDTH = 210;
const MARGIN = 15;
const LINE_HEIGHT = 5;
const LABEL_COL_WIDTH = 52;
const SECTION_BAR_HEIGHT = 7;
const HEADER_GRAY = 230;

function formatDateOnly(dateString) {
  const date = dateString ? new Date(dateString) : new Date();
  return date.toLocaleDateString("pt-BR", {
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
  });
}

function drawSectionBar(pdf, y, text, contentWidth, centerX) {
  pdf.setFillColor(HEADER_GRAY, HEADER_GRAY, HEADER_GRAY);
  pdf.setDrawColor(0);
  pdf.rect(MARGIN, y, contentWidth, SECTION_BAR_HEIGHT, "FD");
  pdf.setFontSize(10);
  pdf.setFont(undefined, "bold");
  pdf.text(text, centerX, y + 4.8, { align: "center" });
  return y + SECTION_BAR_HEIGHT;
}

const LABEL_PADDING = 2;
const VALUE_PADDING = 2;

function splitColumnText(pdf, text, maxWidth, fontStyle) {
  pdf.setFontSize(10);
  pdf.setFont(undefined, fontStyle);
  return pdf.splitTextToSize(String(text ?? ""), maxWidth);
}

function drawTableRow(pdf, y, label, value, contentWidth, valueWidth) {
  const labelMaxWidth = LABEL_COL_WIDTH - LABEL_PADDING * 2;
  const valueMaxWidth = valueWidth - VALUE_PADDING * 2;
  const labelLines = splitColumnText(pdf, label, labelMaxWidth, "bold");
  const valueLines = splitColumnText(pdf, value, valueMaxWidth, "normal");
  const lineCount = Math.max(labelLines.length, valueLines.length);
  const rowHeight = Math.max(8, lineCount * LINE_HEIGHT + 4);
  const valueX = MARGIN + LABEL_COL_WIDTH;

  pdf.setDrawColor(0);
  pdf.rect(MARGIN, y, contentWidth, rowHeight);
  pdf.line(valueX, y, valueX, y + rowHeight);

  pdf.setFontSize(10);
  pdf.setFont(undefined, "bold");
  let labelY = y + 5.5;
  labelLines.forEach((line) => {
    pdf.text(line, MARGIN + LABEL_PADDING, labelY, { maxWidth: labelMaxWidth });
    labelY += LINE_HEIGHT;
  });

  pdf.setFont(undefined, "normal");
  let textY = y + 5.5;
  valueLines.forEach((line) => {
    pdf.text(line, valueX + VALUE_PADDING, textY, { maxWidth: valueMaxWidth });
    textY += LINE_HEIGHT;
  });

  return y + rowHeight;
}

function drawTableRowMultiline(pdf, y, label, valueLines, contentWidth, valueWidth) {
  const labelMaxWidth = LABEL_COL_WIDTH - LABEL_PADDING * 2;
  const valueMaxWidth = valueWidth - VALUE_PADDING * 2;
  const labelLines = splitColumnText(pdf, label, labelMaxWidth, "bold");
  const lines = valueLines.filter(Boolean);
  const wrapped = lines.flatMap((line) =>
    splitColumnText(pdf, line, valueMaxWidth, "normal"),
  );
  const lineCount = Math.max(labelLines.length, wrapped.length);
  const rowHeight = Math.max(8, lineCount * LINE_HEIGHT + 4);
  const valueX = MARGIN + LABEL_COL_WIDTH;

  pdf.setDrawColor(0);
  pdf.rect(MARGIN, y, contentWidth, rowHeight);
  pdf.line(valueX, y, valueX, y + rowHeight);

  pdf.setFontSize(10);
  pdf.setFont(undefined, "bold");
  let labelY = y + 5.5;
  labelLines.forEach((line) => {
    pdf.text(line, MARGIN + LABEL_PADDING, labelY, { maxWidth: labelMaxWidth });
    labelY += LINE_HEIGHT;
  });

  pdf.setFont(undefined, "normal");
  let textY = y + 5.5;
  wrapped.forEach((line) => {
    pdf.text(line, valueX + VALUE_PADDING, textY, { maxWidth: valueMaxWidth });
    textY += LINE_HEIGHT;
  });

  return y + rowHeight;
}

export async function carregarPdfLayoutImagens(processoId) {
  const toDataUrl = (blob) =>
    new Promise((resolve) => {
      const reader = new FileReader();
      reader.onloadend = () => resolve(reader.result);
      reader.readAsDataURL(blob);
    });

  const loadImages = async (layoutId) => {
    const [headerRes, footerRes] = await Promise.all([
      pdfLayoutService.getHeaderImage(layoutId).catch(() => null),
      pdfLayoutService.getFooterImage(layoutId).catch(() => null),
    ]);
    return {
      headerImgData: headerRes ? await toDataUrl(headerRes.data) : null,
      footerImgData: footerRes ? await toDataUrl(footerRes.data) : null,
    };
  };

  try {
    let layoutId = null;
    if (processoId) {
      const res = await pdfLayoutService.obterPorTipoLicenca(processoId);
      layoutId = res.data?.data;
    }
    if (!layoutId) {
      const latestRes = await pdfLayoutService.obterLatest().catch(() => null);
      layoutId = latestRes?.data?.data;
    }
    if (!layoutId) {
      return { headerImgData: null, footerImgData: null };
    }
    return await loadImages(layoutId);
  } catch {
    return { headerImgData: null, footerImgData: null };
  }
}

export function gerarRequerimentoProtocoloPdf({
  processoNome,
  empreendimento,
  especificacao,
  usuario,
  numeroProcesso,
  enderecoFormatado,
  dataProtocolo,
  municipioTenant = "Nova Russas",
  headerImgData,
  footerImgData,
}) {
  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4" });
  const centerX = PAGE_WIDTH / 2;
  const contentWidth = PAGE_WIDTH - MARGIN * 2;
  const valueWidth = contentWidth - LABEL_COL_WIDTH;
  let y = 20;

  const headerHeight = 30;
  const footerHeight = 25;

  if (headerImgData) {
    pdf.addImage(headerImgData, "PNG", 0, 0, PAGE_WIDTH, headerHeight);
    y = headerHeight + 10;
  }

  pdf.setFontSize(16);
  pdf.setFont(undefined, "bold");
  pdf.text("REQUERIMENTO", centerX, y, { align: "center" });
  y += 12;

  if (numeroProcesso) {
    const dataStr = formatDateOnly(dataProtocolo);
    y = drawSectionBar(
      pdf,
      y,
      `PROCESSO: ${numeroProcesso} - ${dataStr}`,
      contentWidth,
      centerX,
    );
  }

  const tipoProcesso = (processoNome || "AUTORIZAÇÃO AMBIENTAL").toUpperCase();
  const grupoAtividade = GRUPO_ATIVIDADE_FLORESTAL_LABEL.toUpperCase();
  const atividadeLabel = getAtividadeEmpreendimentoLabel(
    empreendimento?.atividadeEmpreendimento,
  ).toUpperCase();

  y = drawTableRow(
    pdf,
    y,
    "Tipo de processo:",
    tipoProcesso,
    contentWidth,
    valueWidth,
  );
  y = drawTableRowMultiline(
    pdf,
    y,
    "Atividade:",
    [grupoAtividade, atividadeLabel],
    contentWidth,
    valueWidth,
  );
  y = drawTableRow(
    pdf,
    y,
    "Especificação:",
    (especificacao || "").toUpperCase(),
    contentWidth,
    valueWidth,
  );

  y += 4;
  y = drawSectionBar(pdf, y, "INFORMAÇÕES DO REQUERENTE", contentWidth, centerX);

  const nomeRequerente = usuario
    ? `${usuario.nome || ""} ${usuario.sobrenome || ""}`.trim().toUpperCase()
    : "-";
  const documento = usuario?.cpf ? formatCpfCnpj(usuario.cpf) : "-";
  const contato = usuario?.telefone ? formatPhone(usuario.telefone) : "-";
  const endereco =
    enderecoFormatado || montarEnderecoEmpreendimento(empreendimento);

  y = drawTableRow(pdf, y, "Requerente:", nomeRequerente, contentWidth, valueWidth);
  y = drawTableRow(pdf, y, "CNPJ/CPF:", documento, contentWidth, valueWidth);
  y = drawTableRow(pdf, y, "Contato:", contato, contentWidth, valueWidth);
  y = drawTableRow(
    pdf,
    y,
    "Endereço do empreendimento:",
    endereco,
    contentWidth,
    valueWidth,
  );

  y += 8;
  pdf.setFontSize(11);
  pdf.setFont(undefined, "normal");
  const footerLines = pdf.splitTextToSize(
    "Nestes termos, Peço deferimento.",
    contentWidth,
  );
  footerLines.forEach((line) => {
    pdf.text(line, MARGIN, y);
    y += LINE_HEIGHT;
  });
  y += 7;

  const dataExtenso = new Date(dataProtocolo || Date.now()).toLocaleDateString("pt-BR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  });
  pdf.text(`${municipioTenant}/CE, ${dataExtenso}`, PAGE_WIDTH - MARGIN, y, { align: "right" });
  y += 20;
  pdf.line(PAGE_WIDTH - MARGIN - 70, y, PAGE_WIDTH - MARGIN, y);
  y += 5;
  const nomeAssinatura = usuario
    ? `${usuario.nome || ""} ${usuario.sobrenome || ""}`.trim()
    : "-";
  pdf.text(nomeAssinatura, PAGE_WIDTH - MARGIN - 35, y, { align: "center" });
  y += 5;
  pdf.text("Requerente", PAGE_WIDTH - MARGIN - 35, y, { align: "center" });

  if (footerImgData) {
    pdf.addImage(footerImgData, "PNG", 0, 297 - footerHeight, PAGE_WIDTH, footerHeight);
  }

  return pdf;
}

export async function baixarRequerimentoProtocoloPdf(protocolo, usuario) {
  const { headerImgData, footerImgData } = await carregarPdfLayoutImagens(
    protocolo.processoId,
  );
  const pdf = gerarRequerimentoProtocoloPdf({
    processoNome: protocolo.processoNome,
    empreendimento: {
      atividadeEmpreendimento: protocolo.atividadeEmpreendimento,
    },
    enderecoFormatado: protocolo.empreendimentoEnderecoFormatado,
    especificacao: protocolo.especificacao,
    usuario,
    numeroProcesso: protocolo.numeroProcesso,
    dataProtocolo: protocolo.createAt,
    headerImgData,
    footerImgData,
  });
  pdf.save(`Requerimento-${protocolo.numeroProcesso || protocolo.id}.pdf`);
}
